import {
  type Product,
  type Value,
  type Variant,
  getAttributeValue,
  getPrice,
  getTotalAppliedReductions,
  extendPromise,
  type BaskteItemDisplayDataItem,
} from '@scayle/storefront-nuxt'

import {
  type PreferredDeliveryDate,
  SUBSCRIPTION_CANCELLATION_POLICY,
  SUBSCRIPTION_ELIGIBILITY_ATTRIBUTE_NAME,
  SUBSCRIPTION_INTERVALS_ATTRIBUTE_NAME,
  SUBSCRIPTION_TERM,
  SUBSCRIPTION_DISCOUNT_TEXT,
  SUBSCRIPTION_DISCOUNT_UNIT,
  SUBSCRIPTION_DISCOUNT_VALUE,
  SUBSCRIPTION_SUBSEQUENT_DISCOUNT,
  getOrdinalSuffix,
  getSubscriptionIntervals,
} from '../helpers/subscription'

const selectedIntervals: Record<PropertyKey, Ref<Value | undefined>> = {}
const selectedPreferredDeliveryDates: Record<
  PropertyKey,
  Ref<PreferredDeliveryDate | undefined>
> = {}

export function useSubscription(
  product: MaybeRefOrGetter<Product>,
  pricePromotionKey: MaybeRefOrGetter<string>,
  variants: MaybeRefOrGetter<Variant | Array<Variant | undefined> | undefined>,
  key?: string,
) {
  const { $i18n } = useNuxtApp()
  const id = toValue(product).id

  selectedIntervals[id] = ref<Value | undefined>()
  selectedPreferredDeliveryDates[id] = ref<PreferredDeliveryDate | undefined>()

  const productPromise = useProduct({
    params: {
      id,
      with: {
        variants: {
          attributes: {
            withKey: [
              SUBSCRIPTION_ELIGIBILITY_ATTRIBUTE_NAME,
              SUBSCRIPTION_INTERVALS_ATTRIBUTE_NAME,
              SUBSCRIPTION_TERM,
              SUBSCRIPTION_CANCELLATION_POLICY,
              SUBSCRIPTION_DISCOUNT_TEXT,
              SUBSCRIPTION_DISCOUNT_UNIT,
              SUBSCRIPTION_DISCOUNT_VALUE,
            ],
          },
          advancedAttributes: {
            withKey: [SUBSCRIPTION_SUBSEQUENT_DISCOUNT],
          },
          lowestPriorPrice: true,
        },
      },
      pricePromotionKey: toValue(pricePromotionKey),
    },
    key: `product-subscription-${key}`,
  })

  const { data: subscriptionProduct } = productPromise

  const subscriptionIntervals = computed(() =>
    getSubscriptionIntervals(firstSelectedVariant.value),
  )

  const firstSelectedVariant = computed(() => selectedVariants.value?.at(0))

  const subscriptionTerm = computed(() =>
    getSubscriptionTerm(firstSelectedVariant),
  )

  const subscriptionCancellationPolicy = computed(() =>
    getSubscriptionCancellationPolicy(firstSelectedVariant),
  )

  const basketItemSubscriptionAttributes = computed(() => ({
    customData: customData.value,
    displayData: displayData.value,
  }))

  const itemsToAdd = computed(() => {
    return (
      selectedVariants.value?.map((selectedVariant) => ({
        variantId: selectedVariant?.id,
        quantity: 1,
        customData: customData.value,
        displayData: displayData.value,
      })) ?? []
    )
  })

  const subscriptionDefinition = computed(() => ({
    subscriptionInterval: selectedIntervals[id].value?.value,
    subscriptionDeliveryDate: selectedPreferredDeliveryDates[id].value?.day,
    subscriptionTerm: subscriptionTerm.value?.value,
    subscriptionCancellationPolicy: subscriptionCancellationPolicy.value?.value,
  }))

  const customData = computed(() => {
    return getSubscriptionCustomData({
      pricePromotionKey,
      subscriptionTerm,
      subscriptionCancellationPolicy,
      selectedInterval: selectedIntervals[id],
      selectedPreferredDeliveryDate: selectedPreferredDeliveryDates[id],
    })
  })

  const ordinalSuffixKey = computed(() =>
    getOrdinalSuffix(
      $i18n.locale,
      selectedPreferredDeliveryDates[id].value?.day,
    ),
  )

  const originalPrice = computed(() => {
    const variant = toValue(firstSelectedVariant)
    const originalVariant = toValue(product)?.variants?.find(
      (entry) => entry.id === variant?.id,
    )

    return originalVariant?.price || toValue(product).priceRange?.min
  })

  const subscriptionPrice = computed(() => {
    const variant = toValue(firstSelectedVariant)

    const price = variant ? getPrice(variant) : undefined

    if (!price || price?.appliedReductions?.length) {
      return price
    }

    // compute appliedReductions in case they are missing
    const initialDiscount = computeSubscriptionDiscount(
      originalPrice.value,
      variant?.price,
    )

    if (!initialDiscount) {
      return price
    }

    const appliedReduction = {
      amount: {
        relative: initialDiscount.value / 100,
        absoluteWithTax:
          (originalPrice.value?.withTax ?? 0) - (variant?.price?.withTax ?? 0),
      },
      category: 'promotion',
      type: 'relative',
    }

    return {
      ...price,
      appliedReductions: [appliedReduction],
    } as Variant['price']
  })

  const subscriptionVariantsEligible = computed(() =>
    selectedVariants.value?.every(
      (selectedVariant) =>
        getAttributeValue(
          selectedVariant?.attributes,
          SUBSCRIPTION_ELIGIBILITY_ATTRIBUTE_NAME,
        ) === 'true',
    ),
  )

  const subscriptionDiscounts = computed<{
    subsequent: SubscriptionDiscount | undefined
    initial: SubscriptionDiscount | undefined
  }>(() => {
    const subsequent = getSubsequentDiscountForVariant(firstSelectedVariant)

    const originalVariant = toValue(product)?.variants?.find(
      (entry) => entry.id === firstSelectedVariant.value?.id,
    )
    const initial = computeSubscriptionDiscount(
      originalVariant?.price,
      toValue(firstSelectedVariant)?.price,
    )

    return {
      initial,
      subsequent,
    }
  })

  const totalReductions = computed(
    () =>
      (subscriptionPrice.value &&
        getTotalAppliedReductions(subscriptionPrice.value)?.relative * 100) ||
      0,
  )

  const variantIds = computed(() => {
    const value = toValue(variants)
    if (!value) {
      return []
    }
    if (Array.isArray(value)) {
      return value.filter(Boolean).map((variant) => variant.id)
    }

    return [value.id]
  })

  const selectedVariants = computed(() => {
    return subscriptionProduct.value?.variants?.filter((subscriptionVariant) =>
      toValue(variantIds).includes(subscriptionVariant.id),
    )
  })

  const subsequentPrice = computed(() => {
    const price =
      (!!originalPrice.value?.withTax &&
        !!subscriptionDiscounts.value?.subsequent &&
        // we need to manually calculate subsequent discounts
        calculateDiscountCentAmout(
          originalPrice.value?.withTax,
          subscriptionDiscounts.value?.subsequent,
        )) ||
      originalPrice.value?.withTax

    return roundSubsequentPriceCentAmount(price, $i18n)
  })

  const displayData = computed(() => {
    const displayDataItem = getSubscriptionDisplayDataItem(
      {
        subsequentPrice,
        pricePromotionKey,
        subscriptionTerm,
        subscriptionCancellationPolicy,
        selectedInterval: selectedIntervals[id],
        selectedPreferredDeliveryDate: selectedPreferredDeliveryDates[id],
      },
      $i18n,
    )

    return {
      'attribute-1': displayDataItem,
    }
  })

  return extendPromise(
    productPromise.then(() => ({})),
    {
      subscriptionProduct,
      selectedVariants,
      firstSelectedVariant,
      subscriptionIntervals,
      subscriptionTerm,
      selectedInterval: selectedIntervals[id],
      selectedPreferredDeliveryDate: selectedPreferredDeliveryDates[id],
      subscriptionCancellationPolicy,
      itemsToAdd,
      originalPrice,
      subscriptionPrice,
      subsequentPrice,
      basketItemSubscriptionAttributes,
      subscriptionDefinition,
      subscriptionVariantsEligible,
      totalReductions,
      ordinalSuffixKey,
      subscriptionDiscounts,
    },
  )
}
